"use client";

import { useRef } from "react";
import { useScroll, useSpring, useMotionValueEvent } from "framer-motion";
import { cn } from "@/lib/utils";
import type { VideoSource } from "@/lib/video-utils";

type Props = {
  sources: VideoSource[];
  posterUrl: string | null;
  scrollDistance: number;
  fitClass: string;
};

export function ScrollScrubVideoClient({ sources, posterUrl, scrollDistance, fitClass }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const rafRef = useRef<number | null>(null);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  // Smoothing the raw scroll value avoids per-frame seek thrash when the user flicks the wheel.
  const smooth = useSpring(scrollYProgress, {
    stiffness: 120,
    damping: 30,
    mass: 0.4,
  });

  useMotionValueEvent(smooth, "change", (progress) => {
    const video = videoRef.current;
    if (!video) return;
    const duration = video.duration;
    if (!duration || Number.isNaN(duration)) return;

    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
    }
    rafRef.current = requestAnimationFrame(() => {
      const clamped = Math.min(1, Math.max(0, progress));
      video.currentTime = clamped * duration;
    });
  });

  return (
    <div
      ref={containerRef}
      style={{ height: `${scrollDistance}vh` }}
      className="relative w-full"
    >
      <div className="sticky top-0 h-screen w-full overflow-hidden">
        <video
          ref={videoRef}
          muted
          playsInline
          preload="auto"
          poster={posterUrl ?? undefined}
          className={cn("absolute inset-0 w-full h-full", fitClass)}
        >
          {sources.map((source) => (
            <source key={source.src} src={source.src} type={source.type} />
          ))}
        </video>
      </div>
    </div>
  );
}
